
import BarcodeSymbologiesSelection from '@/components/BarcodeSymbologiesSelection.vue'
import SelectionCameraPopup from '@/components/dialog/SelectionCameraPopup.vue'
import ListStringScanComponent from '@/components/ListStringScanComponent.vue'
import IconSwitch from '@/components/svg/IconSwitch.vue'
import ScanArea from '@/components/svg/ScanArea.vue'
import { openMessage } from '@/utils/helpers'
import { IListStringItem } from 'smartbarcode-web-core/src/utils/types'
import { Options, Vue } from 'vue-class-component'
import { Emit, Prop } from 'vue-property-decorator'

@Options({
  components: {
    BarcodeSymbologiesSelection,
    SelectionCameraPopup,
    IconSwitch,
    ScanArea,
    ListStringScanComponent,
  },
  emits: ['update:modelValue', 'change'],
  inheritAttrs: false,
  name: 'InputListString',
})
export default class InputListString extends Vue {
  @Prop({ type: Array, default: () => [] }) modelValue?: IListStringItem[]
  @Prop({ type: Boolean, default: false }) isScan?: boolean
  @Prop({ type: String, default: '' }) fieldSchema?: string
  @Prop({ type: Number }) readonly minLength?: number
  @Prop({ type: Number }) readonly maxLength?: number
  @Prop({ type: String }) readonly fieldName?: string
  @Prop({ type: String }) readonly error?: string

  dialogVisible = false
  loading = false
  localValue: IListStringItem[] = []

  // Drag and drop state
  isDragging = false
  dragIndex = -1
  dragOverIndex = -1
  inputValue = ''

  // Touch drag state
  touchStartY = 0
  touchCurrentY = 0
  dragStyle = {}

  // Scan mode state
  scanDialogVisible = false
  scanLoading = false
  manualScanValue = ''

  // Camera and barcode scanner state
  isReadyToScan = true
  scannedDialogVisible = false
  checkScannedBarcode = false
  scannedBarcodeList: IListStringItem[] = []

  mounted() {
    this.localValue = [...(this.modelValue || [])]
  }

  get listValue(): IListStringItem[] {
    return this.localValue || []
  }

  get displayValue(): string {
    if (!this.listValue || this.listValue.length === 0) {
      return this.$t('add')
    }
    return this.$t('view')
  }

  get validationError(): string {
    const length = this.listValue.length

    if (this.error) {
      return this.error
    }

    if (this.minLength !== undefined && length < this.minLength) {
      return this.$t('minimum_items_required', { minLength: this.minLength })
    }

    if (this.maxLength !== undefined && length > this.maxLength) {
      return this.$t('maximum_items_required', { maxLength: this.maxLength })
    }

    return ''
  }

  get hasValidationError(): boolean {
    return !!this.validationError
  }

  get wouldExceedMaxLength(): boolean {
    return this.maxLength !== undefined && this.localValue.length >= this.maxLength
  }

  @Emit('update:modelValue')
  updateModelValue(newList: IListStringItem[]) {
    return newList
  }

  @Emit('change')
  emitChange(newList: IListStringItem[]) {
    return newList
  }

  addItem(e: Event) {
    e.preventDefault()
    if (this.inputValue.trim() && !this.wouldExceedMaxLength) {
      const newItem: IListStringItem = {
        value: this.inputValue.trim(),
        createdDateTime: new Date().toISOString(),
      }
      this.localValue = [...this.localValue, newItem]
      this.inputValue = ''
    }
  }

  removeItem(index: number) {
    this.localValue = this.localValue.filter((_, i) => i !== index)
  }

  // Touch-friendly move functions
  moveItem(fromIndex: number, toIndex: number) {
    if (toIndex < 0 || toIndex >= this.localValue.length) return

    const newList = [...this.localValue]
    const [movedItem] = newList.splice(fromIndex, 1)
    newList.splice(toIndex, 0, movedItem)
    this.localValue = newList
  }

  // Drag and drop functions
  startDrag(event: MouseEvent | TouchEvent, index: number) {
    this.isDragging = true
    this.dragIndex = index

    if (event.type === 'touchstart') {
      const touch = (event as TouchEvent).touches[0]
      this.touchStartY = touch.clientY
      this.touchCurrentY = touch.clientY

      // Prevent scrolling while dragging
      document.body.style.overflow = 'hidden'
    }
  }

  handleDragStart(event: DragEvent, index: number) {
    if (!this.isDragging || this.dragIndex !== index) {
      event.preventDefault()
      return
    }

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', index.toString())
    }
  }

  handleDragOver(event: DragEvent, index?: number) {
    event.preventDefault()
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move'
    }
    if (index !== undefined) {
      this.dragOverIndex = index
    }
  }

  handleDrop(event: DragEvent, dropIndex: number) {
    event.preventDefault()

    const draggedIndex = parseInt(event.dataTransfer?.getData('text/plain') || '-1')

    if (draggedIndex !== -1 && draggedIndex !== dropIndex) {
      this.moveItem(draggedIndex, dropIndex)
    }

    this.handleDragEnd()
  }

  handleDragEnd() {
    this.isDragging = false
    this.dragIndex = -1
    this.dragOverIndex = -1
    this.dragStyle = {}

    // Re-enable scrolling
    document.body.style.overflow = ''
  }

  handleTouchMove(event: TouchEvent) {
    if (!this.isDragging) return

    event.preventDefault()
    const touch = event.touches[0]
    this.touchCurrentY = touch.clientY

    const deltaY = this.touchCurrentY - this.touchStartY

    // Update drag style for visual feedback
    this.dragStyle = {
      transform: `translateY(${deltaY}px)`,
      zIndex: 1000,
      opacity: 0.8,
    }

    // Find the element we're hovering over
    const elements = document.elementsFromPoint(touch.clientX, touch.clientY)
    const targetWrapper = elements.find((el) => el.classList.contains('barcode-item-wrapper'))

    if (targetWrapper) {
      const allWrappers = Array.from(document.querySelectorAll('.barcode-item-wrapper'))
      const targetIndex = allWrappers.indexOf(targetWrapper)
      if (targetIndex !== -1 && targetIndex !== this.dragIndex) {
        this.dragOverIndex = targetIndex
      }
    }
  }

  handleTouchEnd(event: TouchEvent) {
    if (!this.isDragging) return

    event.preventDefault()

    // If we have a valid drop target, perform the move
    if (this.dragOverIndex !== -1 && this.dragOverIndex !== this.dragIndex) {
      this.moveItem(this.dragIndex, this.dragOverIndex)
    }

    this.handleDragEnd()
  }

  confirmChanges() {
    this.updateModelValue(this.localValue)
    this.emitChange(this.localValue)
    this.dialogVisible = false
  }

  async cancelChanges() {
    // Check if there are unsaved changes
    const hasChanges = JSON.stringify(this.localValue) !== JSON.stringify(this.modelValue || [])

    if (hasChanges) {
      const originalLength = this.modelValue?.length || 0
      const currentLength = this.localValue.length
      const changesSummary =
        originalLength !== currentLength
          ? ` (${originalLength} → ${currentLength} items)`
          : ` (${currentLength} items modified)`

      try {
        await this.$confirm(
          this.$t('unsaved_changes', { changes: changesSummary }),
          this.$t('title_unsaved_changes', { changes: changesSummary }),
          {
            confirmButtonText: this.$t('discard_changes'),
            cancelButtonText: this.$t('keep_editing'),
            type: 'warning',
            confirmButtonClass: 'el-button--danger',
          }
        )
      } catch (error) {
        return
      }
    }

    // Reset local value to original modelValue
    this.localValue = [...(this.modelValue || [])]
    this.dialogVisible = false
  }

  openDialog() {
    // Reset local value to current modelValue when opening dialog
    this.localValue = [...(this.modelValue || [])]
    this.dialogVisible = true
  }

  // Scan mode methods
  openScanDialog() {
    this.scannedBarcodeList = []
    this.manualScanValue = ''
    this.scanDialogVisible = true
  }

  onScanConfirm(scannedValue: string, scanMode: 'add' | 'remove') {
    if (scannedValue.trim()) {
      if (scanMode === 'add') {
        if (this.wouldExceedMaxLength) {
          openMessage(this.$t('maximum_items_reached', { maxItems: this.maxLength }), 'warning')
          return
        }
        // Add scanned item to the list if it doesn't already exist
        const existingItem = this.localValue.find((item) => item.value === scannedValue.trim())
        if (!existingItem) {
          const newItem: IListStringItem = {
            value: scannedValue.trim(),
            createdDateTime: new Date().toISOString(),
          }
          this.localValue = [...this.localValue, newItem]
          openMessage(this.$t('item_detected'), 'success')
        } else {
          openMessage(this.$t('item_already_scanned'), 'info')
        }
      } else {
        // Remove scanned item from the list
        const index = this.localValue.findIndex((item) => item.value === scannedValue.trim())
        if (index > -1) {
          openMessage(this.$t('item_deleted'), 'success')
          this.localValue.splice(index, 1)
        }
      }
    }
  }

  onCheckScannedBarcode() {
    this.checkScannedBarcode = true
    this.scannedDialogVisible = true
  }

  onCloseScannedDialog() {
    this.scannedDialogVisible = false
    this.checkScannedBarcode = false
  }
}
